import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { shipmentService, uploadImageForShipment } from '@/services/firebase';
import { useAuth } from '@/hooks/useAuth';
import { Shipment } from '@/types';
import LoadingSpinner from '@/components/LoadingSpinner';
import { toast } from 'react-hot-toast';

export default function CustomerShipmentDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const fetchShipment = () => {
    if (!id) return;
    setLoading(true);
    shipmentService.getShipmentById(id)
      .then(data => {
        setShipment(data);
        setError(null);
      })
      .catch(err => {
        console.error("Error fetching shipment details:", err);
        setError("Failed to load shipment details.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchShipment();
  }, [id]);
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !id || !user) return;
    setUploading(true);
    const toastId = toast.loading('Uploading image...');
    
    try {
      await uploadImageForShipment(id, selectedFile);
      toast.success('Image uploaded successfully!', { id: toastId });
      setSelectedFile(null);
      fetchShipment(); // Refresh shipment data to show new image
    } catch (error) {
      console.error(error);
      toast.error('Upload failed. Please try again.', { id: toastId });
    } finally {
      setUploading(false);
    }
  };


  if (loading) return <LoadingSpinner />;
  if (error) return <div className="text-center text-red-500">{error}</div>;
  if (!shipment) return <div className="text-center">Shipment not found.</div>;

  return (
    <div className="container mx-auto p-4 md:p-6">
      <div className="bg-white shadow-lg rounded-lg p-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Shipment Details</h1>
        <p className="text-sm font-semibold text-blue-600 mb-6">Tracking: #{shipment.trackingNumber}</p>
        
        {/* Shipment Info */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <h3 className="font-bold text-gray-700 mb-2">From:</h3>
            <p>{shipment.origin?.street}, {shipment.origin?.city}, {shipment.origin?.state} {shipment.origin?.zipCode}</p>
            <p>{shipment.origin?.country}</p>
          </div>
          <div>
            <h3 className="font-bold text-gray-700 mb-2">To:</h3>
            <p>{shipment.destination?.street}, {shipment.destination?.city}, {shipment.destination?.state} {shipment.destination?.zipCode}</p>
            <p>{shipment.destination?.country}</p>
          </div>
        </div>

        {/* Status and Dates */}
        <div className="border-t border-b border-gray-200 py-4 mb-6">
          <div className="flex justify-around text-center">
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <p className="font-semibold capitalize">{shipment.status.replace('_', ' ')}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Created On</p>
              <p className="font-semibold">{new Date(shipment.createdAt).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Est. Delivery</p>
              <p className="font-semibold">{new Date(shipment.estimatedDeliveryDate).toLocaleDateString()}</p>
            </div>
          </div>
        </div>

        {/* Shipment Items */}
        {shipment.items && shipment.items.length > 0 && (
          <div className="mb-6">
            <h3 className="font-bold text-gray-700 mb-3">Shipment Items</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="space-y-3">
                {shipment.items.map((item, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-white rounded border">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{item.description}</p>
                      <p className="text-sm text-gray-500">
                        Weight: {item.weight} lbs × {item.quantity} = {(item.weight * item.quantity).toFixed(1)} lbs
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-700">
                        {(item.weight * item.quantity).toFixed(1)} lbs
                      </p>
                    </div>
                  </div>
                ))}
                <div className="border-t pt-3 mt-3">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-900">Total Weight:</span>
                    <span className="font-bold text-lg text-gray-900">
                      {shipment.items.reduce((total, item) => total + (item.weight * item.quantity), 0).toFixed(1)} lbs
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Display Uploaded Images */}
        {shipment.images && shipment.images.length > 0 && (
          <div className="my-6">
            <h3 className="font-bold text-gray-700 mb-3">Uploaded Images</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {shipment.images.map((url, index) => (
                <a key={index} href={url} target="_blank" rel="noopener noreferrer">
                  <img src={url} alt={`Shipment item ${index + 1}`} className="w-full h-auto object-cover rounded-lg shadow-md hover:shadow-xl transition-shadow" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Image Upload Section */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-bold text-gray-700 mb-3">Upload Item Image</h3>
          <div className="flex items-center space-x-4">
            <input 
              type="file" 
              onChange={handleFileChange}
              className="file-input file-input-bordered file-input-sm w-full max-w-xs" 
            />
            <button 
              onClick={handleUpload} 
              disabled={!selectedFile || uploading} 
              className="btn btn-primary btn-sm"
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
          {selectedFile && <p className="text-xs text-gray-500 mt-2">Selected: {selectedFile.name}</p>}
        </div>

      </div>
    </div>
  );
} 